import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { Checkbox, Stack, Typography } from '@mui/material';
import { AppIcon, Badge } from 'framework/ui/mui-builder';
import type { BadgeProps } from 'framework/ui/mui-builder';
import type { CandidateCheck, CandidateCheckStatus } from '../../shared/candidateChecks';

const ICON_SX = { fontSize: 20, m: '2px' };

type CheckStatusView = {
  label: string;
  icon: React.ReactNode;
  badgeColor: BadgeProps['color'];
  isNegative?: boolean;
};

const CHECK_STATUS_VIEWS: Record<CandidateCheckStatus, CheckStatusView> = {
  pending: {
    label: 'Pending',
    icon: <Checkbox size="small" checked={false} disabled sx={{ p: 0 }} />,
    badgeColor: 'default',
  },
  inProgress: {
    label: 'In progress',
    icon: <ScheduleOutlinedIcon color="primary" sx={ICON_SX} />,
    badgeColor: 'info',
  },
  submitted: {
    label: 'Submitted',
    icon: <AppIcon icon="verified-document" viewBox="0 0 16 16" color="primary" sx={ICON_SX} />,
    badgeColor: 'info',
  },
  scheduled: {
    label: 'Scheduled',
    icon: <CalendarMonthOutlinedIcon color="warning" sx={ICON_SX} />,
    badgeColor: 'warning',
  },
  passed: {
    label: 'Completed',
    icon: <AppIcon icon="completed-check" viewBox="0 0 24 24" sx={{ ...ICON_SX, color: 'success.light' }} />,
    badgeColor: 'success',
  },
  failed: {
    label: 'Completed',
    icon: <DisabledByDefaultOutlinedIcon color="error" sx={ICON_SX} />,
    badgeColor: 'default',
    isNegative: true,
  },
};

type CandidateChecksProps = {
  checks: CandidateCheck[];
};

const CandidateChecks = ({ checks }: CandidateChecksProps) => {
  const passedCount = checks.filter(check => check.status === 'passed').length;

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">
        {passedCount} of {checks.length} checks completed
      </Typography>

      {checks.map(check => {
        const view = CHECK_STATUS_VIEWS[check.status];

        return (
          <Stack key={check.id} gap={0.25}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              {view.icon}
              <Typography variant="body2">{check.label}</Typography>
              <Badge color={view.badgeColor} sx={{ fontSize: 10 }}>
                {view.label}
              </Badge>
            </Stack>
            <Typography variant="caption" color={view.isNegative ? 'error' : 'textSecondary'}>
              {check.hint}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default CandidateChecks;
